import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { FadeUp } from "../components/Reveal";
import { EmSwash } from "../components/EmSwash";
import { NoteEyebrow } from "../components/NoteEyebrow";
import { isPrototypeMode } from "../components/PrototypeNotice";
import { SEO } from "../lib/SEO";
import { useNoIndex } from "../lib/useNoIndex";
import { useDiscovery } from "../lib/publicCampaigns";
import { LiveCampaignCard } from "../components/browse/LiveCampaignCard";
import { FilterChips } from "../components/browse/FilterChips";
import { LitGround } from "../components/PaperLight";

/** Shown by URL before enough campaigns are live. No index, no grid, no fake cards. */
const NotYet = ({ count, minRequired }) => {
  useNoIndex();
  return (
    <LitGround>
    <section className="mx-auto max-w-3xl px-6 pb-28 pt-40 lg:px-8 lg:pt-52" data-testid="browse-not-yet">
      <NoteEyebrow accent="bg-tangerine">Campaigns</NoteEyebrow>
      <h1 className="mt-6 font-serif text-3xl lg:text-[3.5rem] leading-[1.02] text-ink">
        The first students are <EmSwash>onboarding now.</EmSwash>
      </h1>
      <p className="mt-6 max-w-xl text-base leading-relaxed text-olive">
        Real campaigns appear here the moment our first cohort is verified and live — no earlier. Until then, you can see how a campaign page works in the pilot preview, or give to the fund and we&rsquo;ll get it to verified students the week they launch.
      </p>
      {count > 0 && (
        <p className="mt-3 text-[13px] text-olive" data-testid="browse-not-yet-count">
          {count} of the first {minRequired} campaigns {count === 1 ? "is" : "are"} live.
        </p>
      )}
      <div className="mt-10 flex flex-col gap-3 sm:flex-row">
        <Link to="/demo#campaigns" data-testid="browse-not-yet-preview" className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-7 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest">
          See sample campaigns <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
        </Link>
        <Link to="/#give" data-testid="browse-not-yet-give" className="inline-flex items-center justify-center gap-2 rounded-full border border-ink px-7 py-3.5 text-base font-semibold text-ink transition-colors hover:bg-ink hover:text-sand">
          Give to the fund
        </Link>
      </div>
    </section>
    </LitGround>
  );
};

export default function BrowseCampaigns() {
  const navigate = useNavigate();
  const { loading, visible, count, min_required, items, schools, categories } = useDiscovery();
  const [school, setSchool] = useState(null);
  const [category, setCategory] = useState(null);

  const filtered = useMemo(
    () => items.filter((c) => (!school || c.school === school) && (!category || c.category === category)),
    [items, school, category],
  );
  const schoolCounts = useMemo(() => {
    const m = {};
    items.filter((c) => !category || c.category === category).forEach((c) => { if (c.school) m[c.school] = (m[c.school] || 0) + 1; });
    return m;
  }, [items, category]);
  const categoryCounts = useMemo(() => {
    const m = {};
    items.filter((c) => !school || c.school === school).forEach((c) => { if (c.category) m[c.category] = (m[c.category] || 0) + 1; });
    return m;
  }, [items, school]);

  return (
    <main data-testid="browse-page" className="bg-sand">
      <SEO title="Campaigns — My Rich Aunt" description="Verified students raising for the specific things that keep them enrolled. Every gift is tax-deductible." path="/campaigns" />
      <Navbar onStart={() => navigate("/preview")} onDonate={() => navigate("/#give")} />

      {loading ? (
        <div className="flex min-h-[60vh] items-center justify-center" data-testid="browse-loading"><Loader2 className="h-6 w-6 animate-spin text-olive" /></div>
      ) : !visible ? (
        <NotYet count={count} minRequired={min_required} />
      ) : (
        <>
        <LitGround>
        <section className="mx-auto max-w-7xl px-6 pb-4 pt-40 lg:px-8 lg:pt-52" data-testid="browse-masthead">
          <FadeUp>
            <NoteEyebrow accent="bg-tangerine">{isPrototypeMode() ? "a preview of the student campaigns page" : "Meet the students"}</NoteEyebrow>
            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
              <h1 className="font-serif text-3xl lg:text-[3.5rem] leading-[1.02] text-ink lg:col-span-7">
                {isPrototypeMode() ? <>Sample campaigns, <EmSwash>not live.</EmSwash></> : <>Verified students. <EmSwash>Specific</EmSwash> needs.</>}
              </h1>
              <p className="text-base leading-relaxed text-olive lg:col-span-5">
                {isPrototypeMode()
                  ? "Once we launch, every student here will be enrollment-verified and every page read by a real person before it's published. Gifts are made to My Rich Aunt, which awards every grant."
                  : "Every student here is enrollment-verified, and every page was read by a real person before it went live. Pick someone whose story means something to you as a non-binding preference; My Rich Aunt awards the grant, and you follow the progress."}
              </p>
            </div>
          </FadeUp>
        </section>
        </LitGround>

        <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-8" data-testid="browse-grid-section" data-prototype={isPrototypeMode() ? "true" : "false"}>
          <FadeUp className="mt-8 space-y-3">
            <FilterChips prefix="browse-school" allLabel="All schools" options={schools.map((s) => s.name)} counts={schoolCounts} selected={school} onSelect={setSchool} ariaLabel="Filter by school" />
            <FilterChips prefix="browse-need" allLabel="All needs" options={categories.map((c) => c.name)} counts={categoryCounts} selected={category} onSelect={setCategory} ariaLabel="Filter by type of need" />
          </FadeUp>

          <p className="mt-8 text-[13px] text-olive" data-testid="browse-count">
            Showing <span className="font-semibold text-ink">{filtered.length}</span> of {items.length} {isPrototypeMode() ? "sample" : "live"} {items.length === 1 ? "campaign" : "campaigns"}
            {school && <> at <em className="italic text-ink">{school}</em></>}
            {category && <> · <em className="italic text-ink">{category}</em></>}
          </p>

          <motion.div layout className="mt-6 grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3" data-testid="browse-grid">
            <AnimatePresence mode="popLayout">
              {filtered.map((c) => (
                <motion.div key={c.id} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}>
                  <LiveCampaignCard c={c} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {filtered.length === 0 && (
            <div className="mt-6 rounded-md border border-dashed border-border bg-bone p-10 text-center" data-testid="browse-empty">
              <p className="font-serif text-3xl text-ink">No campaigns match that combination yet.</p>
              <button type="button" onClick={() => { setSchool(null); setCategory(null); }} data-testid="browse-clear-filters" className="mt-4 text-base font-semibold text-tangerine underline underline-offset-4 hover:text-ink">
                Clear filters
              </button>
            </div>
          )}
        </section>
        </>
      )}

      <Footer onStart={() => navigate("/preview")} onDonate={() => navigate("/#give")} />
    </main>
  );
}
